import { useCallback, useEffect } from 'react'
import { usePrevious } from 'react-use'
import { ModelMeshClickListener, useModelClickListenerV2 } from '@scene'
import { useEditElementStore, useModelStore } from '@editorStores'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'

/**
 * Store-driven selection of wall openings during IFC import — the opening counterpart to
 * `useIFCPlanarSelection`. Also shows the wall openings in the scene while enabled so they are
 * visible and clickable.
 *
 * A plain click selects one opening (click it again to clear) and holding the multi-select
 * modifier toggles openings in and out of `multiActiveOpenings`, so a single opening can be
 * edited or several aligned together.
 * Used both by the guided "Öffnungen ausrichten" step and the non-guided planar editing flow.
 */
const useIFCOpeningSelection = (enabled: boolean): void => {
  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const setActiveOpening = useEditElementStore(state => state.setActiveOpening)
  const setMultiActiveOpenings = useEditElementStore(state => state.setMultiActiveOpenings)
  const setShowWallOpenings = useModelStore(state => state.setShowWallOpenings)

  const onOpeningClick = useCallback<ModelMeshClickListener>(
    (openingGuid, event, data) => {
      if (data.type !== 'opening') return

      // Selecting an opening deselects any active planar element so the drawer shows the opening
      // editor rather than the element form (the two selections are mutually exclusive).
      setActiveElement(null)
      setActiveOpening(null)

      const current = useEditElementStore.getState().multiActiveOpenings
      const isMultiSelectPressed = eventMatchesPointerModifier(event?.nativeEvent, 'multiSelect')

      if (isMultiSelectPressed) {
        setMultiActiveOpenings(
          current.includes(openingGuid)
            ? current.filter(guid => guid !== openingGuid)
            : [...current, openingGuid],
        )
      } else {
        setMultiActiveOpenings(
          current.length === 1 && current[0] === openingGuid ? [] : [openingGuid],
        )
      }
    },
    [setActiveElement, setActiveOpening, setMultiActiveOpenings],
  )

  useModelClickListenerV2('opening', onOpeningClick, {
    enabled,
    guids: null,
    enableOnMultiSelect: true,
  })

  useEffect(() => {
    if (!enabled) return
    setShowWallOpenings(true)
    return () => setShowWallOpenings(false)
  }, [enabled, setShowWallOpenings])

  // Drop a stale multi-select when opening selection is switched off — keyed on the transition
  // rather than done in a cleanup, which would also fire on unmount and so discard the selection
  // when leaving the page (it is meant to survive page switches).
  const wasEnabled = usePrevious(enabled)
  useEffect(() => {
    if (wasEnabled && !enabled) setMultiActiveOpenings([])
  }, [enabled, wasEnabled, setMultiActiveOpenings])
}

export default useIFCOpeningSelection
